import { useMutation, useQueryClient } from '@tanstack/react-query'
import { fetcher } from '@quickmd-llc/frontend-utils'
import {
  getGetIntakeformsQueryKey,
  getGetIntakeformsIntakeFormTemplateIdQueryKey,
} from '@/api/generated/intake-form-template/intake-form-template'
import type { IntakeFormTemplateAdminApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { mapIntakeFormError } from './mapIntakeFormError'
import type { IntakeFormRequestBody } from './useCreateIntakeForm'

/**
 * Saves (updates) an existing intake form. Replaces the `saveIntakeForm` thunk +
 * SAVE_INTAKE_FORM* reducer / IntakeFormListener flow.
 *
 * The old flow: SAVE -> setLoading(true); SAVE_..._SUCCESS -> setLoading(false) and merged
 * the response into `intakeFormData[id]`; SAVE_..._ERROR -> setError(mappedMessage). Here
 * `isPending` drives the spinner, `onSuccess` invalidates the per-id detail query (so the
 * view re-reads the saved form) and the collection query (a rename changes the list), and
 * `onError` surfaces the mapped message via the caller's `onError`.
 *
 * As with create, the generated `putIntakeformsIntakeFormTemplateId` does not accept a
 * request body, so we send it through the shared `fetcher`, reusing the generated per-id URL
 * (the last element of the per-id query key) so it stays in sync on regeneration.
 */

const getIntakeFormUrl = (id: string): string => {
  const key = getGetIntakeformsIntakeFormTemplateIdQueryKey(id)
  const url = key[key.length - 1]
  return typeof url === 'string' ? url : ''
}

export const useSaveIntakeForm = (id: string) => {
  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: (body: IntakeFormRequestBody) =>
      fetcher<IntakeFormTemplateAdminApiOutContract>({
        url: getIntakeFormUrl(id),
        method: 'PUT',
        data: body,
      }),
  })

  const saveIntakeForm = (
    body: IntakeFormRequestBody,
    opts?: { onSuccess?: () => void; onError?: (message: string) => void },
  ) => {
    mutate(body, {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetIntakeformsIntakeFormTemplateIdQueryKey(id),
        })
        void queryClient.invalidateQueries({ queryKey: getGetIntakeformsQueryKey() })
        opts?.onSuccess?.()
      },
      onError: (error) => {
        opts?.onError?.(mapIntakeFormError(error))
      },
    })
  }

  return { saveIntakeForm, isPending }
}
